/**
 * DSCDropdownRow.jsx
 *
 * Wraps Group D `SearchableDropdown` with the DSC-specific question label
 * pattern used in every slot card.
 *
 * Citations:
 *  - Group D primitive: `src/components/ui/SearchableDropdown.jsx`
 *    (typeahead + `favoritesSlot` opt-in prop). PORTED VERBATIM as the inner
 *    dropdown — we add ONLY the DSC question-label wrapper.
 *  - Legacy LWC: `dscProductCard.html` lines 252-289 — `<lightning-combobox
 *    label="..." options={...} value={...} onchange={...}>` pattern. Legacy
 *    labels are bound to `data.dropdownPlaceholder` from
 *    `searchResults.js:1043-1172`. PWA mirrors that question-first layout
 *    via `dscSlotConfig.js DSC_SLOT_CONFIG[productLine][slotId].dropdownLabel`.
 *  - Plan §0 row `DSCDropdownRow.jsx`: "wraps `src/components/ui/Searchable
 *    Dropdown.jsx` (already enhanced in SP3.A.2-enhance). Used in every slot
 *    card. Passes `favoritesSlot` for logged-in user favourites."
 *
 * @module components/dsc/DSCDropdownRow
 * @since 2026-05-20 (Group E SP2 — shared DSC primitives)
 */

import SearchableDropdown from '@components/ui/SearchableDropdown';

/**
 * @param {Object} props
 * @param {string} props.question — DSC-style question above the dropdown
 *        (e.g. "Which rock drill are you using?").
 * @param {Array<Object>} props.items — option list passed through to
 *        SearchableDropdown verbatim.
 * @param {Object|null} props.value
 * @param {(item: Object|null) => void} props.onChange
 * @param {boolean} [props.disabled] — disables the dropdown when the upstream
 *        slot is not yet filled (per `useDSCSlot().isDisabled`).
 * @param {string} [props.displayField] default 'label'
 * @param {string[]} [props.searchFields]
 * @param {string} [props.placeholder] default 'Select...'
 * @param {React.ReactNode} [props.favoritesSlot] — rendered above the result
 *        list when present. Used by logged-in users to surface favourites
 *        (brainstorm wishlist #9).
 * @param {string} [props.dataTestid]
 */
export default function DSCDropdownRow({
  question,
  items = [],
  value,
  onChange,
  disabled = false,
  displayField = 'label',
  searchFields,
  placeholder = 'Select...',
  favoritesSlot = null,
  dataTestid = 'dsc-dropdown-row',
}) {
  return (
    <div
      className="flex flex-col gap-2 w-full"
      data-testid={dataTestid}
    >
      {question && (
        <label className="text-sm font-sandvik-text text-carbon-black font-medium">
          {question}
        </label>
      )}
      <SearchableDropdown
        items={items}
        value={value}
        onChange={onChange}
        disabled={disabled}
        displayField={displayField}
        searchFields={searchFields}
        placeholder={placeholder}
        favoritesSlot={favoritesSlot}
      />
    </div>
  );
}
